import AsyncStorage from '@react-native-async-storage/async-storage';
import { getDid } from '../services/bluesky';

const FAVORITES_KEY = 'favorites_users';

export interface FavoriteUser {
  did: string;
  handle: string;
  displayName?: string;
  avatar?: string;
  description?: string;
  addedAt: string;
}

export const getFavorites = async (): Promise<FavoriteUser[]> => {
  try {
    const currentDid = getDid();
    if (!currentDid) return [];
    
    const key = `${FAVORITES_KEY}_${currentDid}`;
    const data = await AsyncStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    return [];
  } catch (error) {
    console.error('Failed to get favorites:', error);
    return [];
  }
};

export const addToFavorites = async (user: Omit<FavoriteUser, 'addedAt'>): Promise<void> => {
  try {
    const currentDid = getDid();
    if (!currentDid) throw new Error('Not logged in');
    
    const key = `${FAVORITES_KEY}_${currentDid}`;
    const favorites = await getFavorites();
    
    // Check if already exists
    if (favorites.some(f => f.did === user.did)) {
      return;
    }
    
    const newFavorite: FavoriteUser = {
      ...user,
      addedAt: new Date().toISOString(),
    };
    
    favorites.push(newFavorite);
    await AsyncStorage.setItem(key, JSON.stringify(favorites));
  } catch (error) {
    console.error('Failed to add to favorites:', error);
    throw error;
  }
};

export const removeFromFavorites = async (did: string): Promise<void> => {
  try {
    const currentDid = getDid();
    if (!currentDid) throw new Error('Not logged in');
    
    const key = `${FAVORITES_KEY}_${currentDid}`;
    const favorites = await getFavorites();
    const filtered = favorites.filter(f => f.did !== did);
    await AsyncStorage.setItem(key, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to remove from favorites:', error);
    throw error;
  }
};

export const isFavorite = async (did: string): Promise<boolean> => {
  try {
    const favorites = await getFavorites();
    return favorites.some(f => f.did === did);
  } catch (error) {
    console.error('Failed to check favorite:', error);
    return false;
  }
};
